// ============================================================================
// ShareProfile — a reusable "share my public profile" affordance.
// ----------------------------------------------------------------------------
// Encapsulates the two ways a trainer shares their /t/{handle} page:
//   • the native Web Share API (navigator.share) where available (mobile, most
//     modern browsers) — opens the OS share sheet so the link can go to any app;
//   • a copy-link fallback (navigator.clipboard) everywhere else, with a manual
//     "select & copy" hint as a last resort.
// `useShareProfile()` is the headless hook (logic only); <ShareButtons/> is the
// ready-made button row; <ShareProfileCard/> is the full Pulse card used on the
// trainer dashboard. The profile editor (ProfilePage) renders its own publish/
// share section, so this is the SECOND, lighter-weight surface (spec §9 item 7:
// shareable from the editor AND/OR the dashboard).
//
// All copy comes from the `trainerProfile` i18n namespace (en + zh-Hant). Built
// on the locked Pulse design system; nothing in the design system is restyled.
// ============================================================================
import { useCallback, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Badge, Button, Card } from "../components";

/** Result of the share hook — call `share()` / `copy()`; render `copied`. */
export interface ShareControls {
  /** True for ~1.8s right after a successful copy (drives the "Copied!" label). */
  copied: boolean;
  /** True when an error occurred (clipboard blocked) — show the manual hint. */
  failed: boolean;
  /** Copy the URL to the clipboard. No-op when there's no URL. */
  copy: () => Promise<void>;
  /** Native share where available, else copy. No-op when there's no URL. */
  share: () => Promise<void>;
}

/**
 * Headless share logic for a public-profile URL. `title` / `text` enrich the
 * native share sheet; both are optional. Safe to call with a null/empty url
 * (every action becomes a no-op), so callers can render disabled affordances
 * before the page is publishable.
 */
export function useShareProfile(
  url: string | null | undefined,
  meta?: { title?: string; text?: string },
): ShareControls {
  const [copied, setCopied] = useState(false);
  const [failed, setFailed] = useState(false);

  const copy = useCallback(async () => {
    if (!url) return;
    setFailed(false);
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch {
      setFailed(true);
    }
  }, [url]);

  const share = useCallback(async () => {
    if (!url) return;
    setFailed(false);
    const nav = navigator as Navigator & {
      share?: (data: ShareData) => Promise<void>;
    };
    if (nav.share) {
      try {
        await nav.share({ title: meta?.title, text: meta?.text, url });
        return;
      } catch {
        // User cancelled or the share was rejected — fall through to copy so
        // the action is never a dead end.
      }
    }
    await copy();
  }, [url, meta?.title, meta?.text, copy]);

  return { copied, failed, copy, share };
}

/** The share-action button row (Share + Copy + optional View). */
export function ShareButtons({
  url,
  controls,
  showView = true,
  className,
}: {
  url: string;
  controls: ShareControls;
  showView?: boolean;
  className?: string;
}) {
  const { t } = useTranslation("trainerProfile");
  return (
    <div className={"flex flex-col gap-2 sm:flex-row " + (className ?? "")}>
      <Button
        size="sm"
        className="w-full sm:w-auto"
        onClick={() => void controls.share()}
      >
        {t("share.shareCta")}
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="w-full sm:w-auto"
        onClick={() => void controls.copy()}
      >
        {controls.copied ? t("share.copied") : t("share.copy")}
      </Button>
      {showView && (
        <a
          href={url}
          target="_blank"
          rel="noreferrer"
          className="w-full sm:ml-auto sm:w-auto"
        >
          <Button size="sm" variant="secondary" className="w-full sm:w-auto">
            {t("share.viewCta")}
          </Button>
        </a>
      )}
    </div>
  );
}

/**
 * The dashboard share card. Renders the live link + share/copy/view when the
 * profile is publishable (public_url present), or a gentle "set up your public
 * profile" call-to-action that deep-links into the editor otherwise.
 */
export function ShareProfileCard({
  publicUrl,
  displayName,
}: {
  publicUrl: string | null | undefined;
  displayName?: string | null;
}) {
  const { t } = useTranslation("trainerProfile");
  const controls = useShareProfile(publicUrl, {
    title: t("share.shareTitle", { name: displayName || t("title") }),
    text: t("share.shareText"),
  });

  // Not yet publishable → invite the trainer to set it up.
  if (!publicUrl) {
    return (
      <Card className="space-y-4">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <h3 className="font-display text-lg font-bold text-body">
              {t("share.title")}
            </h3>
            <p className="mt-1 text-sm text-body-muted">{t("share.setupHint")}</p>
          </div>
          <ShareIcon />
        </div>
        <Link to="/app/account/profile" className="block sm:inline-block">
          <Button size="sm" className="w-full sm:w-auto">
            {t("share.setupCta")}
          </Button>
        </Link>
      </Card>
    );
  }

  return (
    <Card className="space-y-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="font-display text-lg font-bold text-body">
              {t("share.title")}
            </h3>
            <Badge tone="success" dot>
              {t("publish.on")}
            </Badge>
          </div>
          <p className="mt-1 text-sm text-body-muted">
            {t("share.dashboardSubtitle")}
          </p>
        </div>
        <ShareIcon />
      </div>

      <div className="flex items-center gap-2 overflow-hidden rounded-md bg-surface-sunken px-3 py-2.5 ring-1 ring-line">
        <span className="truncate text-sm font-medium text-body" dir="ltr">
          {publicUrl}
        </span>
      </div>

      <ShareButtons url={publicUrl} controls={controls} />

      {controls.failed && (
        <p className="text-sm font-medium text-danger">{t("share.copyFailed")}</p>
      )}
    </Card>
  );
}

function ShareIcon() {
  return (
    <span className="grid h-10 w-10 shrink-0 place-items-center rounded-full bg-violet-soft text-ember-600">
      <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" aria-hidden>
        <circle cx="18" cy="5" r="2.6" stroke="currentColor" strokeWidth="1.9" />
        <circle cx="6" cy="12" r="2.6" stroke="currentColor" strokeWidth="1.9" />
        <circle cx="18" cy="19" r="2.6" stroke="currentColor" strokeWidth="1.9" />
        <path
          d="M8.3 10.8l7.4-4.3M8.3 13.2l7.4 4.3"
          stroke="currentColor"
          strokeWidth="1.9"
          strokeLinecap="round"
        />
      </svg>
    </span>
  );
}
